import { useState, useEffect, useRef, useCallback } from "react";
import io from "socket.io-client";
import { socketConfig } from "../../../utils/authConfig";
import { toast } from "react-toastify";

/**
 * @typedef {object} SttSegmentPayload
 * @property {string} transcript - Final delta or interim (non-final) text from server
 * @property {string} [transcriptCumulative] - All final tokens this session (Soniox)
 */

/**
 * @param {object} [options]
 * @param {(payload: SttSegmentPayload) => void} [options.onFinalSegment]
 * @param {() => void} [options.onStreamExpired] - Stop mic + release audio (parent owns recording UI)
 */
export const useSpeechRecognition = ({ onFinalSegment, onStreamExpired } = {}) => {
  const [streamingTranscript, setStreamingTranscript] = useState("");

  const onFinalSegmentRef = useRef(onFinalSegment);
  onFinalSegmentRef.current = onFinalSegment;

  const onStreamExpiredRef = useRef(onStreamExpired);
  onStreamExpiredRef.current = onStreamExpired;

  const isConnected = useRef(false);
  const socketRef = useRef(null);
  const listenersSetupRef = useRef(false);
  const streamExpiryHandledRef = useRef(false);

  const handleStreamExpired = useCallback(() => {
    if (streamExpiryHandledRef.current) return;
    streamExpiryHandledRef.current = true;

    toast.info("Session expired, please tap to start dictation again.");
    onStreamExpiredRef.current?.();

    if (socketRef.current?.connected) {
      socketRef.current.disconnect();
    }
  }, []);

  const setupSocketListeners = useCallback(
    (socket) => {
      if (listenersSetupRef.current && socket === socketRef.current) {
        return;
      }

      if (socket !== socketRef.current && socketRef.current) {
        socketRef.current.removeAllListeners();
        listenersSetupRef.current = false;
      }

      socket.on("connect", () => {
        isConnected.current = true;
        streamExpiryHandledRef.current = false;
        console.log("✅ Socket connected");
      });

      socket.on("connect_error", (error) => {
        isConnected.current = false;
        console.error("❌ Socket connection error:", error);
      });

      socket.on("disconnect", () => {
        isConnected.current = false;
        console.log("🔌 Socket disconnected");
      });

      socket.on(
        "transcription",
        ({
          transcript,
          transcriptCumulative,
          isFinal,
          error: transcriptionError,
        } = {}) => {
          if (transcriptionError === "streamExpired") {
            handleStreamExpired();
            return;
          }

          if (isFinal) {
            onFinalSegmentRef.current?.({
              transcript: String(transcript ?? ""),
              transcriptCumulative:
                transcriptCumulative != null
                  ? String(transcriptCumulative)
                  : undefined,
            });
            setStreamingTranscript("");
            return;
          }

          setStreamingTranscript(String(transcript ?? ""));
        },
      );

      listenersSetupRef.current = true;
    },
    [handleStreamExpired],
  );

  const createSocket = useCallback(() => {
    if (!socketRef.current) {
      const newSocket = io(socketConfig.url, {
        ...socketConfig.options,
        autoConnect: false,
      });
      socketRef.current = newSocket;
      setupSocketListeners(newSocket);
    }
    return socketRef.current;
  }, [setupSocketListeners]);

  useEffect(() => {
    createSocket();

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current.removeAllListeners();
        socketRef.current = null;
        listenersSetupRef.current = false;
      }
    };
  }, [createSocket]);

  const connectSocket = useCallback(async () => {
    createSocket();

    if (socketRef.current.connected) {
      return socketRef.current;
    }

    console.log("🔌 Connecting socket...");
    socketRef.current.connect();

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error("Connection timeout"));
      }, 10000);

      socketRef.current.once("connect", () => {
        clearTimeout(timeout);
        resolve(socketRef.current);
      });

      socketRef.current.once("connect_error", (error) => {
        clearTimeout(timeout);
        reject(error);
      });
    });
  }, [createSocket]);

  const disconnectSocket = useCallback(() => {
    if (socketRef.current?.connected) {
      console.log("🔌 Disconnecting socket...");
      socketRef.current.disconnect();
    }
  }, []);

  return {
    streamingTranscript,
    setStreamingTranscript,
    connectSocket,
    disconnectSocket,
    socketRef,
  };
};
